import React, { useCallback, useEffect, useState } from "react";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import ReportTable from "../../../common/ReportTable/ReportTable";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import { useEpsfStudySite } from "../epsfLedgerUi";
import {
  confirmImpTransferArrival,
  createImpTransfer,
  despatchImpTransfer,
  fetchEpsfStudyConfig,
  fetchImpBalances,
  fetchImpKits,
  fetchImpTransfers,
} from "../epsfService";
import "../EpsfPages.scss";

const FIELD_OPTIONS = [
  { key: "TransferReference", label: "Reference" },
  { key: "Status", label: "Status" },
  { key: "TotalQuantity", label: "Qty" },
  { key: "DespatchedAt", label: "Despatched" },
  { key: "ReceivedAt", label: "Received" },
  { key: "Actions", label: "Actions" },
];

const DEFAULT_COLUMNS = [
  "TransferReference",
  "Status",
  "TotalQuantity",
  "DespatchedAt",
  "ReceivedAt",
  "Actions",
];

export default function ImpTransfer() {
  const { loading, canView, moduleAccess, canReceive } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState("");
  const [supplyModel, setSupplyModel] = useState("DirectToSite");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    toSiteId: "",
    impBatchId: "",
    quantity: "",
    transferReference: "",
    notes: "",
    selectedKitIds: [],
    storageUnitId: "",
  });
  const [balances, setBalances] = useState([]);
  const [kits, setKits] = useState([]);

  const selectedBatch = balances.find(
    (b) => String(b.ImpBatchId || b.impBatchId) === String(form.impBatchId),
  );
  const isIndividualKit =
    String(
      selectedBatch?.InventoryTrackingMethod ||
        selectedBatch?.inventoryTrackingMethod ||
        "",
    ) === "IndividualKit";

  const availableKits = kits.filter((k) =>
    ["Available", "Released"].includes(String(k.Status || k.status || "")),
  );

  const refresh = useCallback(async () => {
    setError("");
    if (!ctx.studyId) {
      setRows([]);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const [cfg, list, bal] = await Promise.all([
        fetchEpsfStudyConfig(ctx.studyId),
        fetchImpTransfers(ctx.studyId, ctx.siteId),
        ctx.siteId
          ? fetchImpBalances(ctx.studyId, ctx.siteId)
          : Promise.resolve({ success: true, rows: [] }),
      ]);
      if (cfg.success) {
        const row = cfg.rows?.[0] || {};
        setSupplyModel(row.SupplyModel || row.supplyModel || "DirectToSite");
      }
      if (list.success) setRows(list.rows || []);
      else if (list.message) setError(list.message);
      if (bal.success) setBalances(bal.rows || []);
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ctx.studyId || !ctx.siteId || !form.impBatchId || !isIndividualKit) {
        setKits([]);
        return;
      }
      const result = await fetchImpKits(ctx.studyId, ctx.siteId, {
        impBatchId: form.impBatchId,
      });
      if (!cancelled && result.success) setKits(result.rows || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.studyId, ctx.siteId, form.impBatchId, isIndividualKit]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canReceive || busy) return;
    if (!ctx.studyId || !ctx.siteId) {
      setError("Select study and site (from / hub) in the breadcrumb bar.");
      return;
    }
    if (!form.toSiteId || !form.impBatchId) {
      setError("To site and batch are required.");
      return;
    }
    if (isIndividualKit) {
      if (!form.selectedKitIds.length) {
        setError(
          "Select Available kits to transfer. Empty selection is not allowed.",
        );
        return;
      }
    } else if (!form.quantity || !(Number(form.quantity) > 0)) {
      setError("Quantity is required.");
      return;
    }
    const batch = selectedBatch;
    setBusy(true);
    setError("");
    try {
      const qty = isIndividualKit
        ? form.selectedKitIds.length
        : Number(form.quantity);
      const result = await createImpTransfer({
        StudyId: ctx.studyId,
        FromSiteId: ctx.siteId,
        ToSiteId: form.toSiteId,
        ImpProductId: batch?.ImpProductId || batch?.impProductId,
        ImpBatchId: form.impBatchId,
        Quantity: qty,
        TransferReference: form.transferReference || null,
        Notes: form.notes || "",
        KitIdsJson: isIndividualKit
          ? JSON.stringify(form.selectedKitIds)
          : null,
      });
      if (!result.success) {
        setError(result.message || "Could not create transfer.");
        return;
      }
      setForm({
        toSiteId: "",
        impBatchId: "",
        quantity: "",
        transferReference: "",
        notes: "",
        selectedKitIds: [],
        storageUnitId: "",
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onDespatch = useCallback(async (row) => {
    const id = row.TransferId || row.transferId;
    if (!id || busy) return;
    setBusy(true);
    try {
      const result = await despatchImpTransfer({
        StudyId: ctx.studyId,
        TransferId: id,
      });
      if (!result.success) setError(result.message || "Despatch failed.");
      else await refresh();
    } finally {
      setBusy(false);
    }
  }, [busy, ctx.studyId, refresh]);

  const onConfirm = useCallback(async (row) => {
    const id = row.TransferId || row.transferId;
    if (!id || busy) return;
    if (!form.storageUnitId) {
      setError("Destination storage location is required for arrival confirmation.");
      return;
    }
    setBusy(true);
    try {
      const result = await confirmImpTransferArrival({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        TransferId: id,
        StorageUnitId: form.storageUnitId,
      });
      if (!result.success) setError(result.message || "Arrival confirm failed.");
      else await refresh();
    } finally {
      setBusy(false);
    }
  }, [busy, ctx.studyId, ctx.siteId, form.storageUnitId, refresh]);

  const toggleKit = (kitId) => {
    setForm((prev) => {
      const set = new Set(prev.selectedKitIds);
      if (set.has(kitId)) set.delete(kitId);
      else set.add(kitId);
      const ids = Array.from(set);
      return { ...prev, selectedKitIds: ids, quantity: String(ids.length) };
    });
  };

  const renderCell = useCallback(
    (row, field) => {
      if (field === "TransferReference") {
        return row.TransferReference || row.transferReference || "—";
      }
      if (field === "Status") return row.Status || row.status || "—";
      if (field === "TotalQuantity") {
        const v = row.TotalQuantity ?? row.totalQuantity;
        return v == null ? "—" : String(v);
      }
      if (field === "DespatchedAt" || field === "ReceivedAt") {
        const v =
          row[field] || row[field.charAt(0).toLowerCase() + field.slice(1)];
        return v ? String(v).replace("T", " ").slice(0, 16) : "—";
      }
      if (field === "Actions") {
        const status = row.Status || row.status;
        return (
          <span className="d-inline-flex gap-1">
            {status === "Prepared" ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={!canReceive || busy}
                onClick={() => onDespatch(row)}
              >
                Despatch
              </button>
            ) : null}
            {status === "InTransit" || status === "Despatched" ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                disabled={!canReceive || busy}
                onClick={() => onConfirm(row)}
              >
                Confirm arrival
              </button>
            ) : null}
          </span>
        );
      }
      return "—";
    },
    [busy, canReceive, onConfirm, onDespatch],
  );

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Hub transfer" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: refresh },
          ]}
        />

        <p className="epsf-list-page__hint">
          Study supply model:{" "}
          <strong>
            {supplyModel === "ViaHub"
              ? "Via Hub / Central Pharmacy"
              : "Direct to Site"}
          </strong>
          . Hub transfers require Via Hub. Arrival confirmation posts Transfer-In
          at the destination site (not a sponsor receipt).
        </p>

        {error ? <p className="epsf-list-page__error">{error}</p> : null}

        {supplyModel === "ViaHub" && canReceive ? (
          <form className="card card-body mb-3" onSubmit={onCreate}>
            <div className="row g-2">
              <div className="col-md-3">
                <label className="form-label">To site id</label>
                <input
                  className="form-control"
                  value={form.toSiteId}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, toSiteId: e.target.value }))
                  }
                  placeholder="Destination SiteId GUID"
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Batch</label>
                <select
                  className="form-control"
                  value={form.impBatchId}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      impBatchId: e.target.value,
                      selectedKitIds: [],
                      quantity: "",
                    }))
                  }
                  required
                >
                  <option value="">Select batch</option>
                  {balances.map((b) => {
                    const id = String(b.ImpBatchId || b.impBatchId);
                    const label = [
                      b.ProductCode || b.productCode,
                      b.BatchNumber || b.batchNumber,
                      `avail ${
                        b.AvailableOnHand ??
                        b.availableOnHand ??
                        b.OnHand ??
                        b.onHand ??
                        0
                      }`,
                    ]
                      .filter(Boolean)
                      .join(" / ");
                    return (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              {isIndividualKit ? (
                <div className="col-md-4">
                  <label className="form-label">
                    Kits (qty = {form.selectedKitIds.length})
                  </label>
                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: 120, overflow: "auto" }}
                  >
                    {availableKits.length ? (
                      availableKits.map((k) => {
                        const id = String(k.ImpKitId || k.impKitId);
                        return (
                          <label key={id} className="d-block small mb-1">
                            <input
                              type="checkbox"
                              className="me-2"
                              checked={form.selectedKitIds.includes(id)}
                              onChange={() => toggleKit(id)}
                            />
                            {k.KitNumber || k.kitNumber}
                          </label>
                        );
                      })
                    ) : (
                      <span className="small text-muted">No Available kits</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="col-md-2">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    className="form-control"
                    value={form.quantity}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, quantity: e.target.value }))
                    }
                    required
                  />
                </div>
              )}
              <div className="col-md-2">
                <label className="form-label">Reference</label>
                <input
                  className="form-control"
                  value={form.transferReference}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      transferReference: e.target.value,
                    }))
                  }
                  placeholder="Optional"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Dest. storage id *</label>
                <input
                  className="form-control"
                  value={form.storageUnitId}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, storageUnitId: e.target.value }))
                  }
                  placeholder="Required for arrival"
                  required
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={busy}
                >
                  Prepare transfer
                </button>
              </div>
            </div>
          </form>
        ) : null}

        <div className="epsf-report-wrap">
          <ReportTable
            rows={!ctx.studyId ? [] : rows}
            loading={listLoading}
            fieldOptions={FIELD_OPTIONS}
            defaultColumns={DEFAULT_COLUMNS}
            getFieldLabel={(field) =>
              FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderCell}
            getRowKey={(row, index) =>
              String(row.TransferId || row.transferId || index)
            }
            persistLayoutKey="epsf-imp-transfers-v1"
            emptyMessage={
              !ctx.studyId
                ? "Select a study in the breadcrumb bar."
                : supplyModel !== "ViaHub"
                  ? "Set study supply model to Via Hub to use hub transfers."
                  : "No transfers yet."
            }
            pageSizeOptions={[10, 25, 50]}
            columnSearchMode="client"
          />
        </div>
      </div>
    </EpsfAccessGate>
  );
}
